/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { classes, Slot } from '@adobe-commerce/elsie/lib';
import { Button, InLineAlert } from '@adobe-commerce/elsie/components';
import { Form } from '@/account/components';
import { AddressFormWrapperProps, FieldsProps } from '@/account/types';
import { useText } from '@adobe-commerce/elsie/i18n';
import { useAddressForm } from '@/account/hooks/containers/useAddressForm';
import '@/account/components/AddressFormWrapper/AddressFormWrapper.css';
import { transformDefaultValuesAddresses } from '@/account/lib/transformDefaultValuesAddresses';

export const AddressFormWrapper: FunctionComponent<AddressFormWrapperProps> = ({
  hideActionFormButtons,
  formName = '',
  showFormLoader = false,
  showSaveCheckBox = false,
  saveCheckBoxValue = false,
  forwardFormRef,
  slots,
  addressesFormTitle,
  className,
  addressFormId,
  inputsDefaultValueSet,
  showShippingCheckBox = true,
  showBillingCheckBox = true,
  shippingCheckBoxValue = true,
  billingCheckBoxValue = true,
  isOpen,
  fieldIdPrefix,
  onSubmit,
  onCloseBtnClick,
  onSuccess,
  onError,
  onChange,
}) => {
  const translations = useText({
    secondaryButton: 'Account.AddressForm.formText.secondaryButton',
    primaryButton: 'Account.AddressForm.formText.primaryButton',
    saveAddressBook: 'Account.AddressForm.formText.saveAddressBook',
  });

  const {
    inLineAlert,
    addressId,
    submitLoading,
    normalizeFieldsConfig,
    handleUpdateAddress,
    handleCreateAddress,
    handleOnCloseForm,
    handleSaveCheckBoxAddress,
    saveCheckBoxAddress,
    handleInputChange,
    regionOptions,
  } = useAddressForm({
    showFormLoader,
    addressFormId,
    inputsDefaultValueSet: transformDefaultValuesAddresses(
      inputsDefaultValueSet
    ),
    shippingCheckBoxValue,
    billingCheckBoxValue,
    showShippingCheckBox,
    showBillingCheckBox,
    saveCheckBoxValue,
    showSaveCheckBox,
    onSuccess,
    onError,
    onCloseBtnClick,
    formName,
  });

  const [formKey, setFormKey] = useState(0);

  // Re-render form when inputsDefaultValueSet changes
  useEffect(() => {
    setFormKey((prevKey) => prevKey + 1);
  }, [inputsDefaultValueSet]);

  if (!isOpen) return null;

  return (
    <div className={classes(['account-address-form-wrapper', className])}>
      {addressesFormTitle ? (
        <div
          className="account-address-form-wrapper__title"
          data-testid="addressesFormTitle"
        >
          {addressesFormTitle}
        </div>
      ) : null}
      {inLineAlert.text ? (
        <InLineAlert
          data-testid="inLineAlert"
          className="account-address-form-wrapper__notification"
          type={inLineAlert.type}
          variant="secondary"
          heading={inLineAlert.text}
          icon={inLineAlert.icon}
        />
      ) : null}
      <Form
        key={formKey}
        regionOptions={regionOptions}
        forwardFormRef={forwardFormRef}
        slots={slots}
        className="account-address-form"
        name={formName || 'addressesForm'}
        fieldsConfig={normalizeFieldsConfig as FieldsProps[]}
        onSubmit={
          onSubmit || (addressId ? handleUpdateAddress : handleCreateAddress)
        }
        setInputChange={handleInputChange}
        loading={submitLoading}
        showFormLoader={showFormLoader}
        showSaveCheckBox={showSaveCheckBox}
        handleSaveCheckBoxAddress={handleSaveCheckBoxAddress}
        saveCheckBoxAddress={saveCheckBoxAddress}
        onChange={onChange}
        fieldIdPrefix={fieldIdPrefix}
      >
        {addressId ? (
          <input
            type="hidden"
            name="addressId"
            value={addressId}
            data-testid="hidden_test_id"
          />
        ) : null}
        {!hideActionFormButtons ? (
          <div
            className={classes([
              'dropin-field account-address-form-wrapper__buttons',
              [
                'account-address-form-wrapper__buttons--empty',
                showSaveCheckBox,
              ],
            ])}
          >
            {slots?.AddressFormActions ? (
              <Slot
                data-testid="addressFormActions"
                name="AddressFormActions"
                slot={slots.AddressFormActions}
                context={{
                  handleUpdateAddress,
                  handleCreateAddress,
                  addressId,
                }}
              />
            ) : (
              <>
                {!showSaveCheckBox ? (
                  <>
                    <Button
                      // @ts-ignore
                      type={'button'}
                      onClick={handleOnCloseForm}
                      variant="secondary"
                      disabled={submitLoading}
                    >
                      {translations.secondaryButton}
                    </Button>
                    <Button disabled={submitLoading}>
                      {translations.primaryButton}
                    </Button>
                  </>
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </Form>
    </div>
  );
};
